import { Container, Divider, FormControlLabel, Grid, MenuItem, Radio, RadioGroup, Select } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'
import FlexView from 'react-flexview/lib'
import { awaitWithLoading, CentPretty, KPretty } from '../../../services/utils'
import { OptionalTag } from '../../Core/OptionalTag'
import { Section, SectionRadioButton, SectionSelect, SectionSpecialSelect } from '../../Core/Form/FormComponents'
import { MotorcycleCoveragesForm } from '../../Motorcycle/MotorcycleCoveragesForm'
import { ExpansionForm } from '../../Core/Expansion/ExpansionForm'
import { RectangleFrame } from '../../Core/Frames/RectangleFrame'
import { MotorcycleBlockView } from '../../Motorcycle/MotorcycleBlockView'
import { SessionContextStore } from '../../../context/SessionContext'
import { BuyingInsuranceContextStore } from '../../../context/BuyingInsuranceContext'
import { Action } from '../../../models/Action'
import { BackButton } from '../../Core/Buttons/BackButton'
import { ContinueButton } from '../../Core/Buttons/ContinueButton'
import { merge } from 'merge-anything'
import { EventEmitterService, EventKey } from '../../../services/EventEmitterService'
import coveragesApi from '../../../api/coverages.api'
import { ReactComponent as Shield } from '../../../assets/summary_shield.svg'
import { ReactComponent as Motorcycle } from '../../../assets/motorcycle.svg'
import { reportGTM } from '../../Core/Utilities/GTagManger'
import { BillingPeriod } from '../../../models/insurance-models/InsuranceBillingPeriodModel'
import { isMobile } from 'react-device-detect'
import './CustomizeCoverages.scss'
import { StatesContextStore } from '../../../context/StatesContext'
import { flowType } from '../../../Constants'

export const CustomizeCoverages = ({ midtermEditing, SAVE_EVENT_KEY }) => {
  const sessionContext = React.useContext(SessionContextStore)
  const offersContext = React.useContext(BuyingInsuranceContextStore)
  const [isBIPDError, setIsBIPDError] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState('')
  const statesContext = useContext(StatesContextStore)
  const onPolicyFieldChange = (name, value) => {
    let changed = { ...offersContext.offerCart }
    changed[name] = value
    offersContext.dispatch(
      new Action('CART_CHANGED', {
        newOffer: changed,
        isNewFlow: sessionContext.newPaymentFlow,
        administrativeDivision: sessionContext.user.administrativeDivision,
      })
    )
  }

  const getCoverageTooltip = coverageName => {
    let value = offersContext.options[coverageName]
    return value && value.length > 1 ? value[0].tooltip : undefined
  }

  const onDiminishingDeductibleChange = (motorcycleId, value) => {
    let changed = { ...offersContext.offerCart }
    changed.vehicle_requests[motorcycleId].is_diminishing_deductible = value
    offersContext.dispatch(
      new Action('CART_CHANGED', {
        newOffer: changed,
        isNewFlow: sessionContext.newPaymentFlow,
        administrativeDivision: sessionContext.user.administrativeDivision,
      })
    )
  }

  const onMotorcycleChange = (motorcycleId, name, value) => {
    let changed = { ...offersContext.offerCart }
    changed.vehicle_requests[motorcycleId].coverages[name] = value
    offersContext.dispatch(
      new Action('CART_CHANGED', {
        newOffer: changed,
        isNewFlow: sessionContext.newPaymentFlow,
        administrativeDivision: sessionContext.user.administrativeDivision,
      })
    )
  }
  const onContinue = () => {
    EventEmitterService.dispatch(EventKey.FLOW_SUMMARY_FINISH)
  }

  const onMidTermAccept = async () => {
    let res = await awaitWithLoading(coveragesApi.bindMidtermOffer(offersContext.offerId, offersContext.activeInsurance.insuranceId))
    if (res.ok) {
      EventEmitterService.dispatch(SAVE_EVENT_KEY)
    }
  }

  let vehicles = midtermEditing ? offersContext.activeInsurance.insuredVehicles : sessionContext.user.userVehicles

  useEffect(() => {
    let bipdError = false

    if (
      sessionContext.user.administrativeDivision == 'TX' &&
      Object.keys(offersContext.offerCardErrors).length != 0 &&
      Object.keys(offersContext.offerCardErrors['policy']).length != 0 &&
      Object.keys(offersContext.offerCardErrors['policy']).includes('bodily_injury_property_damage')
    ) {
      bipdError = true
    }

    setIsBIPDError(bipdError)
  }, [offersContext.offerCardErrors['policy']])

  useEffect(() => {
    if (midtermEditing) {
      if (sessionContext.activeInsurance != null && sessionContext.activeInsurance.billingPeriod) {
        setBillingPeriod(sessionContext.activeInsurance.billingPeriod)
      } else if (offersContext.billingPeriod) {
        setBillingPeriod(offersContext.billingPeriod)
      } else {
        setBillingPeriod(BillingPeriod.MONTH)
      }
    }
  }, [midtermEditing])

  return (
    <>
      <FlexView className="margin-bottom-basic" vAlignContent="center">
        <Shield className="medium-icon" />
        <div className="text-title blue bold margin-left-8">Policy Coverages</div>
      </FlexView>
      {offersContext.offerCart.bodily_injury_property_damage && (
        <SectionSpecialSelect
          onChange={onPolicyFieldChange}
          name="bodily_injury_property_damage"
          GTMname="Liability-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={
            Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['bodily_injury_property_damage']
          }
          value={offersContext.offerCart.bodily_injury_property_damage}
          buttons={offersContext.options.bodily_injury_property_damage.filter(
            option => !(sessionContext.user.state === 'VA' && option.name === '$30k/$60k/$20k')
          )}
          tooltip={getCoverageTooltip('bodily_injury_property_damage')}
          title="Liability (bodily injury, passenger liability & property damage)"
          subtitle="Pays for other’s damages and injuries if you’re at fault in an accident."
        />
      )}
      {offersContext.offerCart.bodily_injury_liability && (
        <SectionSpecialSelect
          onChange={onPolicyFieldChange}
          name="bodily_injury_liability"
          GTMname="Liability-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['bodily_injury_liability']}
          value={offersContext.offerCart.bodily_injury_liability}
          buttons={offersContext.options.bodily_injury_liability}
          tooltip={getCoverageTooltip('bodily_injury_liability')}
          title="Liability (bodily injury, property damage & passenger liability)"
          subtitle="Pays for other’s damages and injuries if you’re at fault in an accident."
        />
      )}
      {offersContext.offerCart.bodily_injury_underinsured && (
        <SectionSpecialSelect
          name="bodily_injury_underinsured"
          onChange={onPolicyFieldChange}
          GTMname="UIM-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['bodily_injury_underinsured']}
          value={offersContext.offerCart.bodily_injury_underinsured}
          buttons={offersContext.options.bodily_injury_underinsured}
          tooltip={getCoverageTooltip('bodily_injury_underinsured')}
          title="Underinsured Motorist Bodily Injury"
          subtitle="Protects you from injuries/death if you are involved in an accident with an underinsured motorist."
        />
      )}
      {offersContext.offerCart.bodily_injury_uninsured && (
        <SectionSpecialSelect
          name="bodily_injury_uninsured"
          onChange={onPolicyFieldChange}
          GTMname="UM-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['bodily_injury_uninsured']}
          value={offersContext.offerCart.bodily_injury_uninsured}
          buttons={offersContext.options.bodily_injury_uninsured.filter(
            option => !(sessionContext.user.state === 'VA' && (option.name === '$30k/$60k/$20k' || option.name === '$30k/$60k/$20k Alternative'))
          )}
          tooltip={getCoverageTooltip('bodily_injury_uninsured')}
          title={
            sessionContext.user.state === 'VA' ? 'Uninsured Motorist Bodily Injury & Property Damage' : 'Uninsured Motorist Bodily Injury'
          }
          subtitle="Protects you from injuries/death if you are involved in an accident with an uninsured motorist."
        />
      )}
      {offersContext.offerCart.bodily_injury_uninsured_underinsured && (
        <SectionSpecialSelect
          name="bodily_injury_uninsured_underinsured"
          onChange={onPolicyFieldChange}
          GTMname="UM/UIM-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={
            Object.keys(offersContext.offerCardErrors).length == 0 &&
            offersContext.offerCardWarnings['bodily_injury_uninsured_underinsured']
          }
          value={offersContext.offerCart.bodily_injury_uninsured_underinsured}
          buttons={offersContext.options.bodily_injury_uninsured_underinsured}
          tooltip={getCoverageTooltip('bodily_injury_uninsured_underinsured')}
          title="Uninsured/Underinsured Motorist Bodily Injury"
          subtitle="Protects you from injuries/death if you are involved in an accident with an uninsured/underinsured motorist."
        />
      )}
      {offersContext.offerCart.bodily_injury_enhanced_underinsured && (
        <SectionSpecialSelect
          name="bodily_injury_enhanced_underinsured"
          onChange={onPolicyFieldChange}
          GTMname="EUIM limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={
            Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['bodily_injury_enhanced_underinsured']
          }
          value={offersContext.offerCart.bodily_injury_enhanced_underinsured}
          buttons={offersContext.options.bodily_injury_enhanced_underinsured}
          tooltip={getCoverageTooltip('bodily_injury_enhanced_underinsured')}
          title="Enhanced Underinsured Motorist Bodily Injury"
          subtitle="Protects you from injuries/death if you are involved in an accident with an uninsured/underinsured motorist."
        />
      )}
      {offersContext.offerCart.personal_injury_protection && (
        <SectionSpecialSelect
          name="personal_injury_protection"
          onChange={onPolicyFieldChange}
          GTMname="PIP-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['personal_injury_protection']}
          value={offersContext.offerCart.personal_injury_protection}
          buttons={offersContext.options.personal_injury_protection}
          tooltip={getCoverageTooltip('personal_injury_protection')}
          title="Personal Injury Protection (PIP)"
          subtitle="This coverage covers your medical expenses, regardless of who was at fault for an accident."
        />
      )}
      {offersContext.offerCart.medical_payments && (
        <SectionSelect
          name="medical_payments"
          onChange={onPolicyFieldChange}
          GTMname="Medpay-limit"
          errors={offersContext.offerCardErrors['policy']}
          value={offersContext.offerCart.medical_payments}
          buttons={offersContext.options.medical_payments}
          tooltip={getCoverageTooltip('medical_payments')}
          title="Medical Payment"
          subtitle="Helps you pay for doctor and hospital bills if you are injured while using your insured vehicle."
        />
      )}

      {offersContext.offerCart.income_loss_benefits && (
        <SectionSelect
          name="income_loss_benefits"
          onChange={onPolicyFieldChange}
          GTMname="INCMLOS-limit"
          errors={offersContext.offerCardErrors['policy']}
          value={offersContext.offerCart.income_loss_benefits}
          buttons={offersContext.options.income_loss_benefits}
          tooltip={getCoverageTooltip('income_loss_benefits')}
          title="Income Loss Benefits"
          subtitle="Helps you pay for doctor and hospital bills if you are injured while using your insured vehicle."
        />
      )}
      {offersContext.offerCart.rental_reimbursement && (
        <SectionSpecialSelect
          name="rental_reimbursement"
          onChange={onPolicyFieldChange}
          GTMname="Rent-limit"
          errors={offersContext.offerCardErrors[Object.keys(offersContext.offerCart.vehicle_requests)[0]]}
          warnings={Object.keys(offersContext.offerCardErrors).length == 0 && offersContext.offerCardWarnings['rental_reimbursement']}
          value={offersContext.offerCart.rental_reimbursement}
          buttons={offersContext.options.rental_reimbursement}
          tooltip={getCoverageTooltip('rental_reimbursement')}
          title="Transportation Expenses"
          subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
        />
      )}

      {offersContext.offerCart.pedestrian_personal_injury_protection && (
        <SectionSpecialSelect
          name="pedestrian_personal_injury_protection"
          onChange={onPolicyFieldChange}
          GTMname="PedPip-limit"
          errors={offersContext.offerCardErrors['policy']}
          warnings={
            Object.keys(offersContext.offerCardErrors).length == 0 &&
            offersContext.offerCardWarnings['pedestrian_personal_injury_protection']
          }
          value={offersContext.offerCart.pedestrian_personal_injury_protection}
          buttons={offersContext.options.pedestrian_personal_injury_protection}
          tooltip={getCoverageTooltip('pedestrian_personal_injury_protection')}
          title="Pedestrian Personal Injury Protection (PED PIP)"
          subtitle="This coverage pays for basic economic loss including medical expenses, work loss, death benefits, and other reasonable and necessary expenses."
        />
      )}
      <FlexView className="margin-bottom-basic" vAlignContent="center">
        <Motorcycle className="medium-icon" />
        <div className="text-title blue bold margin-left-8">Motorcycle Coverages</div>
      </FlexView>

      {offersContext.offerCart.uninsured_motorist_property_damage &&
        offersContext.options.uninsured_motorist_property_damage &&
        offersContext.options.uninsured_motorist_property_damage.some(x => x.type === 'policy') &&
        offersContext.options.uninsured_motorist_property_damage.length > 1 && (
          <SectionSelect
            onChange={onPolicyFieldChange}
            name="uninsured_motorist_property_damage"
            title="UM Property Damage"
            GTMname="UM-motorist-limit"
            errors={offersContext.offerCardErrors['policy']}
            value={offersContext.offerCart.uninsured_motorist_property_damage}
            buttons={offersContext.options.uninsured_motorist_property_damage}
            tooltip={getCoverageTooltip('uninsured_motorist_property_damage')}
            warnings={
              Object.keys(offersContext.offerCardErrors).length == 0 &&
              offersContext.offerCardWarnings['uninsured_motorist_property_damage']
            }
            subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
          />
        )}

      {offersContext.offerCart.enhanced_underinsured_motorist_property_damage &&
        offersContext.options.enhanced_underinsured_motorist_property_damage &&
        offersContext.options.enhanced_underinsured_motorist_property_damage.some(x => x.type === 'policy') && (
          <SectionSelect
            onChange={onPolicyFieldChange}
            name="enhanced_underinsured_motorist_property_damage"
            title="Enhanced UIM Property Damage"
            GTMname="EUIM-motorist-limit"
            errors={offersContext.offerCardErrors['policy']}
            value={offersContext.offerCart.enhanced_underinsured_motorist_property_damage}
            buttons={offersContext.options.enhanced_underinsured_motorist_property_damage}
            tooltip={getCoverageTooltip('enhanced_underinsured_motorist_property_damage')}
            subtitle="Helps you to pay for vehicle/personal property damage in case of an accident with uninsured guilty motorist."
          />
        )}

      {offersContext.offerCart.uninsured_underinsured_motorist_property_damage && (
        <SectionSpecialSelect
          name="uninsured_underinsured_motorist_property_damage"
          onChange={onPolicyFieldChange}
          GTMname="UM/UIM-property-damage"
          errors={!isBIPDError && offersContext.offerCardErrors['policy']}
          warnings={
            Object.keys(offersContext.offerCardErrors).length == 0 &&
            offersContext.offerCardWarnings['uninsured_underinsured_motorist_property_damage']
          }
          value={offersContext.offerCart.uninsured_underinsured_motorist_property_damage}
          buttons={offersContext.options.uninsured_underinsured_motorist_property_damage}
          tooltip={getCoverageTooltip('uninsured_underinsured_motorist_property_damage')}
          title="Uninsured/Underinsured Property Damage"
          subtitle="If your insured vehicle or personal property is damaged in an accident with a motorist that is either: at-fault motorist who has no insurance to cover your expense; Or by: an at-fault motorist who has not enough liability insurance to cover your expenses."
        />
      )}

      {offersContext.offerCart.roadside_assistance && offersContext.options.roadside_assistance.some(x => x.type === 'policy') && (
        <SectionSelect
          onChange={onPolicyFieldChange}
          name="roadside_assistance"
          title="Roadside Assistance (Add-on Service)"
          GTMname="Roadside_Assistance"
          errors={offersContext.offerCardErrors['policy']}
          value={offersContext.offerCart.roadside_assistance}
          buttons={offersContext.options.roadside_assistance}
          tooltip={getCoverageTooltip('roadside_assistance')}
          subtitle="Get help when you need it most! Limited to 3 calls a year, including up to 20 miles of tows per call with no impact on policy renewal rates. Ride with peace of mind knowing you’re covered in case of breakdowns, flat tires, empty tank and jump starts. This is a non-refundable coverage."
        />
      )}

      {offersContext.offerCart.vehicle_requests &&
        vehicles.map((motorcycle, index) => (
          <div key={index} className="margin-bottom-basic">
            <ExpansionForm
              defaultExpanded
              accordionTile={
                <>
                  <MotorcycleBlockView motorcycle={motorcycle} />
                </>
              }
              formComponent={
                <MotorcycleCoveragesForm
                  onChange={onMotorcycleChange}
                  onDiminishingDeductibleChange={onDiminishingDeductibleChange}
                  isNewFlow={sessionContext.newPaymentFlow}
                  isDiminishingDeductible={offersContext.offerCart.vehicle_requests[motorcycle.id].is_diminishing_deductible}
                  values={offersContext.offerCart.vehicle_requests[motorcycle.id].coverages}
                  options={offersContext.options}
                  errors={offersContext.offerCardErrors[motorcycle.id]}
                  motorcycleId={motorcycle.id}
                  shouldHaveDivider={true}
                />
              }
            />
          </div>
        ))}
      {sessionContext.newPaymentFlow && isMobile && !!offersContext.pricebreakdown.vehiclesDetailsForUi && (
        <>
          <Divider />
          <RectangleFrame className={'tooltip_container'}>
            <div>
              The amount that is <b>Due Today</b> is based on the selected payment recurrence and includes an <b>upfront payment</b> for the
              initial miles of that period - based on the expected miles amount ridden by the insured motorcycle type multiplied by the per
              mile rate. It will serve as credited miles, upon initial odometer photo upload:
            </div>
            {offersContext.pricebreakdown.vehiclesDetailsForUi.map((vehicle, i) => (
              <div key={i} className="margin-top-8">
                <span className="bold">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </span>{' '}
                <br />
                Calculated with{' '}
                {offersContext.billingFlowType === flowType.ESTIMATED_MILEAGE
                  ? vehicle.estimatedBillingPeriodMileage
                  : vehicle.defaultMonthlyMiles}{' '}
                miles multiplied by the per mile rate of: {CentPretty(vehicle.perMilePrice)}
              </div>
            ))}
          </RectangleFrame>
        </>
      )}

      {midtermEditing && offersContext.pricebreakdown.fixedPrice != undefined ? (
        <>
          <div className="margin-top-basic margin-bottom-basic text-body black bold">
            {billingPeriod == BillingPeriod.THREEMONTHS ? 'Periodically' : 'Monthly'} fixed price: $
            {offersContext.pricebreakdown.fixedPrice.toFixed(2)}
          </div>
          <div className="margin-bottom-basic text-body black bold">
            Per mile charge: {CentPretty(offersContext.pricebreakdown.perMileCharge)}
          </div>
          <ContinueButton onClick={onMidTermAccept} />
        </>
      ) : (
        <>
          <div className="margin-top-basic margin-bottom-basic"></div>
          <div className="margin-bottom-basic"></div>
        </>
      )}
    </>
  )
}
